import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

interface UseElementInViewOptions {
  /** Grows or shrinks the observed viewport box, e.g. `'-80px 0px'`. */
  rootMargin?: string;
  /**
   * Value returned before the observer's first callback. Default `false`.
   * Pass `true` when a wrong first frame would flash UI on screen.
   */
  initial?: boolean;
}

/**
 * True while the referenced element intersects the viewport.
 *
 * Options are destructured to primitives so the effect's dependencies stay
 * stable — an `IntersectionObserverInit` object literal would tear down and
 * rebuild the observer on every render.
 *
 * Note: if `ref.current` is null when the effect first runs, this returns
 * `initial` forever — the effect does not re-run on ref assignment. Fine for a
 * target rendered unconditionally alongside the hook; not for a lazy one.
 */
export const useElementInView = (
  ref: RefObject<Element | null>,
  { rootMargin = '0px', initial = false }: UseElementInViewOptions = {},
): boolean => {
  const [isInView, setIsInView] = useState(initial);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => setIsInView(entry.isIntersecting), {
      rootMargin,
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isInView;
};
